// components/parcours-client/RecapSubscriptionStep.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Divider,
  Button,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

const RecapSubscriptionStep = ({ formData, setFormData, handleBack, handleNext }) => {
  const { t } = useTranslation("parcours-client/ReacapSubscriptionStep");

  const [selectedDay, setSelectedDay] = useState(formData.selectedDay || "");
  const [startDate, setStartDate] = useState(formData.startDate || "");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!formData.startDate) {
      const today = new Date().toISOString().split("T")[0];
      setStartDate(today);
      setFormData((prev) => ({ ...prev, startDate: today }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate]);

  const handleSubmit = () => {
    const newErrors = {};
    if (!selectedDay) newErrors.selectedDay = t("errors.selectedDay");
    if (!startDate) newErrors.startDate = t("errors.startDate");
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setFormData((prev) => ({ ...prev, selectedDay, startDate }));
      handleNext();
    }
  };

  // Ouvre un PDF base64 (desktop: nouvel onglet, mobile: téléchargement)
  const openPdf = (base64Data, filename = "document.pdf") => {
    let clean = base64Data.includes(",") ? base64Data.split(",")[1] : base64Data;
    clean = clean.replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");

    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isMobile = isIOS || isAndroid;

    let bytes;
    try {
      const byteChars = atob(clean);
      bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    } catch {
      return;
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    if (isMobile) {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.target = "_self";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      return;
    }

    const win = window.open("", "_blank");
    if (win) {
      win.opener = null;
      win.location.href = blobUrl;
    } else {
      window.location.href = blobUrl;
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };

  const getMaxDate = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    return nextMonth.toISOString().split("T")[0];
  };

  const getToday = () => new Date().toISOString().split("T")[0];

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#4eb933" },
      "&:hover fieldset": { borderColor: "#4eb933" },
      "&.Mui-focused fieldset": { borderColor: "#4eb933" }
    }
  };

  // Parse "Nr rue / Nr local" depuis ClientStep
  const parseNrAdresse = (str) => {
    if (!str || typeof str !== "string") return { streetNumber: "", localNumber: "" };
    const [nrRue = "", nrLocal = ""] = str.split("/").map((s) => s.trim());
    return { streetNumber: nrRue, localNumber: nrLocal };
  };

  const { streetNumber, localNumber } = useMemo(
    () => parseNrAdresse(formData.nrAdresse),
    [formData.nrAdresse]
  );

  const prélèvementDays = [1, 5, 10, 15, 20, 25];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 6 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "900px",
          borderRadius: "20px",
          px: { xs: 3, md: 6 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          backgroundColor: "#F8F8F8",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {/* Titre */}
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2rem", md: "2.4rem" },
            color: "#000",
            mb: 3,
            textAlign: "center",
          }}
        >
          {t("title.part1")}{" "}
          <Box component="span" sx={{ color: "#4eb933" }}>
            {t("title.highlight")}
          </Box>
        </Typography>

        {/* Choix jour de prélèvement */}
        <Box sx={{ mb: 5 }}>
          <Typography sx={{ mb: 2, fontWeight: 600, fontSize: "1rem" }}>
            {t("debitDay.title")} *
          </Typography>

          <Box
            sx={{
              display: "flex",
              border: "1px solid #4eb933",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {prélèvementDays.map((day, index) => (
              <Box
                key={day}
                onClick={() => setSelectedDay(day)}
                sx={{
                  flex: 1,
                  py: 2,
                  cursor: "pointer",
                  textAlign: "center",
                  borderRight: index < prélèvementDays.length - 1 ? "1px solid #4eb933" : "none",
                  backgroundColor: selectedDay === day ? "#4eb933" : "#fff",
                  color: selectedDay === day ? "#fff" : "#4eb933",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  "&:hover": {
                    backgroundColor: selectedDay === day ? "#3e9e2a" : "#f0fff0",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {errors.selectedDay && (
            <Typography sx={{ color: "red", fontSize: "0.8rem", mt: 1 }}>
              {errors.selectedDay}
            </Typography>
          )}

          <Typography sx={{ mt: 1, fontSize: "0.8rem", color: "#666" }}>
            {t("debitDay.help")}
          </Typography>
        </Box>

        {/* Date de début */}
        <Box sx={{ mb: 5 }}>
          <Typography sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}>
            {t("startDate.title")} *
          </Typography>
          <TextField
            fullWidth
            type="date"
            label={t("startDate.label") + " *"}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            error={!!errors.startDate}
            helperText={errors.startDate}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: getToday(), max: getMaxDate() }}
            sx={fieldStyle}
          />
          <Typography sx={{ mt: 1, fontSize: "0.85rem", color: "#666" }}>
            {t("startDate.help")}
          </Typography>
        </Box>

        {/* Récapitulatif */}
        <Typography fontWeight={700} fontSize="1.1rem" mb={1}>
          {t("recap.title")}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Produit sélectionné */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={600} mb={1}>
            {t("product.selected")}
          </Typography>

          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "#f4f4f4",
              mb: 2,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Grid item>
                <Typography fontWeight={700} fontSize="1.1rem" color="#4eb933">
                  {formData.assurance?.nom}
                </Typography>
                <Typography fontSize="0.9rem" color="#444">
                  {formData.assurance?.description}
                </Typography>
              </Grid>
              <Grid item textAlign="right">
                <Typography fontWeight={800} fontSize="1.5rem" color="#4eb933">
                  {formData.assurance?.prix}€<Box component="span" fontSize="0.9rem"> {t("product.perMonth")}</Box>
                </Typography>
                <Typography fontSize="0.85rem" color="#888">
                  {t("product.commitment")} {formData.assurance?.engagement} {t("product.months")}
                </Typography>
              </Grid>
            </Grid>

            <Typography fontWeight={700} fontSize="0.95rem" mb={1}>
              {t("product.coverageIncluded")}
            </Typography>
            <Box sx={{ mb: 1 }}>
              {(formData.assurance?.features || []).map((label, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <CheckCircleIcon sx={{ color: "#4eb933", fontSize: "1rem", mr: 1 }} />
                  <Typography fontSize="0.9rem">{label}</Typography>
                </Box>
              ))}
            </Box>

            {formData.assurance?.priseEnCharge && (
              <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Grid item>
                  <Typography fontSize="0.85rem" color="#000">
                    {t("product.coverageLimit")}{" "}
                    <Box component="span" fontWeight={700}>
                      {formData.assurance.priseEnCharge}€
                    </Box>
                  </Typography>
                </Grid>

                <Grid item>
                  <Box sx={{ display: "flex", gap: 3 }}>
                    {formData.assurance?.pdfIpid && (
                      <Typography
                        sx={{ fontSize: "0.85rem", color: "#4eb933", cursor: "pointer" }}
                        onClick={() => openPdf(formData.assurance.pdfIpid, "IPID.pdf")}
                      >
                        {t("product.ipid")}
                      </Typography>
                    )}
                    {formData.assurance?.pdfNotice && (
                      <Typography
                        sx={{ fontSize: "0.85rem", color: "#4eb933", cursor: "pointer" }}
                        onClick={() => openPdf(formData.assurance.pdfNotice, "Notice.pdf")}
                      >
                        {t("product.notice")}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Box>

        {/* Souscripteur */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={600} mb={1}>
            {t("subscriber.title")}
          </Typography>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, backgroundColor: "#f4f4f4" }}>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.lastName")} :</strong> {formData.nom}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.firstName")} :</strong> {formData.prenom}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.email")} :</strong> {formData.email}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.phone")} :</strong> {formData.telephone}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.address")} :</strong>{" "}
              {`${formData.adresse || ""}${streetNumber ? " " + streetNumber : ""}${localNumber ? " / " + localNumber : ""}`}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.city")} :</strong> {`${formData.codePostal || ""} ${formData.ville || ""}`}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("subscriber.country")} :</strong> {formData.pays}
            </Typography>
            <Typography>
              <strong>{t("subscriber.pesel")} :</strong> {formData.pesel}
            </Typography>
          </Paper>
        </Box>

        {/* Infos vélo */}
        <Box>
          <Typography fontWeight={600} mb={1}>
            {t("bike.title")}
          </Typography>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, backgroundColor: "#f4f4f4" }}>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("bike.type")} :</strong> {formData.bikeType}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("bike.brand")} :</strong> {formData.marque}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("bike.model")} :</strong> {formData.modele}
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("bike.price")} :</strong> {formData.prix} €
            </Typography>
            <Typography sx={{ mb: 1.2 }}>
              <strong>{t("bike.purchaseDate")} :</strong> {formData.dateAchat}
            </Typography>
            <Typography>
              <strong>{t("bike.serial")} :</strong> {formData.numeroSerie}
            </Typography>
          </Paper>
        </Box>

        {/* Navigation */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: { xs: "center", sm: "space-between" },
            gap: 2,
            mt: 5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBackIosIcon />}
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              fontWeight: 600,
              fontSize: { xs: "0.95rem", md: "1rem" },
              py: 1.2,
            }}
          >
            {t("nav.prev")}
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
            fullWidth
            sx={{
              backgroundColor: "#4eb933",
              color: "#fff",
              "&:hover": { backgroundColor: "#3e9e2a" },
              fontWeight: 700,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: { xs: "0.95rem", md: "1rem" },
              py: 1.2,
              px: 3,
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            {t("nav.next")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default RecapSubscriptionStep;